import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'underscore';
import { GreenroomState as GS } from '../../../../stores/types/types';
import { CUSTOM_DESTINATION_OPTIONS, Destination } from '../../../../stores/types/broadcasting';
import {
	AddDestination,
	DeleteDestination,
	UpdateBroadcastResolution,
	UpdateDestination,
} from '../../../../connection/broadcasting';
import { RESOLUTIONS } from '../../../../stores/reducers/settings';
import Switch from '../../../general-components/switch';
import Icon, {
	ICONS,
	COLORS,
} from '../../../general-components/icon';
import {
	OpenButton,
	StyledButton,
	StyledInput,
	StyledLabel,
	StyledLi,
	StyledSelect,
	StyledSpan,
	StyledUl,
} from '../debug-ui';

const baseDestination: Destination = {
	name: '',
	url: '',
	streamKey: '',
	logo: '',
	type: 0,
	selected: false,
};

interface DestinationUIProps {
	destination: Destination | null;
}

function DestinationUI({ destination }: DestinationUIProps) {
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);

	const [destState, setDestState] = useState(destination ?? baseDestination);
	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(false);

	const {
		name,
		url,
		streamKey,
		type,
		selected,
	} = destState;

	const isDefault = useMemo(() => !!(destination?.isBlp ?? !destination?.id), [destination]);
	const isAdding = useMemo(() => !destination, [destination]);
	const canEdit = useMemo(() => editing || isAdding, [editing, isAdding]);

	const handleAddDestination = async () => {
		if (!token || !meeting) return;
		await AddDestination(token, meeting.meeting, destState);
	};

	const handleUpdateDestination = async () => {
		if (!token || !meeting || !destination) return;
		await UpdateDestination(token, meeting.meeting, destState);
		setEditing(false);
	};

	const handleDeleteDestination = async (destinationId: number) => {
		if (!token || !meeting) return;
		await DeleteDestination(token, meeting.meeting, destinationId);
	};

	useEffect(() => {
		if (!isEqual(destination, destState)) {
			setDestState(destination ?? baseDestination);
		}
	}, [destination]);

	return (
		<StyledUl>
			<OpenButton onClick={() => setOpen(!open)}>
				<Icon
					name={open ? ICONS.KEYBOARD_ARROW_DOWN : ICONS.KEYBOARD_ARROW_UP}
					size={14}
					color={COLORS.WHITE}
				/>
			</OpenButton>
			{open ? (
				<>
					<StyledLi>
						<StyledLabel>NAME: </StyledLabel>
						<StyledInput
							type="text"
							value={isDefault ? 'Brandlive' : name}
							onChange={(e) => setDestState({ ...destState, name: e.target.value })}
							disabled={isDefault || !canEdit}
						/>
					</StyledLi>
					<StyledLi>
						<StyledLabel>RTMP ADDRESS: </StyledLabel>
						<StyledInput
							type="text"
							value={url}
							onChange={(e) => setDestState({ ...destState, url: e.target.value })}
							disabled={isDefault || !canEdit}
						/>
					</StyledLi>
					<StyledLi>
						<StyledLabel>STREAM KEY: </StyledLabel>
						<StyledInput
							type="text"
							value={streamKey}
							onChange={(e) => setDestState({ ...destState, streamKey: e.target.value })}
							disabled={isDefault || !canEdit}
						/>
					</StyledLi>
					<StyledLi>
						<StyledLabel>TYPE: </StyledLabel>
						<StyledSelect
							value={CUSTOM_DESTINATION_OPTIONS.find((op) => op.value === type)?.value ?? -1}
							onChange={(e) => setDestState({ ...destState, type: Number(e.target.value) })}
							disabled={isDefault || !canEdit}
						>
							{Object.values(CUSTOM_DESTINATION_OPTIONS).map((res, index) => (
								<option key={index} value={res.value}>
									{res.label}
								</option>
							))}
						</StyledSelect>
					</StyledLi>
					<li className="flex-row justify-content-space-between height-32">
						<div className="flex-align-center">
							<StyledLabel>SELECTED: </StyledLabel>
							<Switch
								on={selected}
								onChange={() => setDestState({ ...destState, selected: !selected })}
								className={`green margin-left-small ${!canEdit && 'disabled'}`}
							/>
						</div>
						<div className="flex-row gap-small">
							{isAdding ? (
								<StyledButton className="background-green text-dark" onClick={handleAddDestination}>
									Add
								</StyledButton>
							) : (
								<>
									<StyledButton
										className="background-green text-dark"
										onClick={editing ? handleUpdateDestination : () => setEditing(true)}
									>
										{editing ? 'Save' : 'Edit'}
									</StyledButton>
									{editing && (
										<StyledButton
											className="background-dark text-white"
											onClick={() => setEditing(false)}
										>
											Cancel
										</StyledButton>
									)}
								</>
							)}
							{!isAdding && !isDefault && (
								<StyledButton
									className="background-green text-dark margin-left-small"
									onClick={() => handleDeleteDestination(destination?.id ?? 0)}
								>
									Delete
								</StyledButton>
							)}
						</div>
					</li>
				</>
			) : (
				<StyledLi>
					{isAdding ? (
						<StyledLabel>ADD DESTINATION</StyledLabel>
					) : (
						<>
							<StyledLabel>NAME: </StyledLabel>
							<StyledSpan>{isDefault ? 'Brandlive' : name}</StyledSpan>
						</>
					)}
				</StyledLi>
			)}
		</StyledUl>
	);
}

export default function DebugSimulcast() {
	const destinations = useSelector((s: GS) => s.BroadcastReducer.destinations);
	const resolution = useSelector((s: GS) => s.BroadcastReducer.resolution);
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);

	const handleChangeResolution = async (e: React.ChangeEvent<HTMLSelectElement>) => {
		if (!token || !meeting) return;
		await UpdateBroadcastResolution(token, meeting.meeting, e.target.value);
	};

	return (
		<div className="section settings">
			<h5>Simulcast</h5>
			<div className="advanced-settings-container">
				<div className="margin-bottom-medium">
					<StyledLabel>RESOLUTION: </StyledLabel>
					<StyledSelect value={resolution} onChange={handleChangeResolution}>
						{Object.keys(RESOLUTIONS).map((res, index) => (
							<option key={index} value={res}>
								{res}
							</option>
						))}
					</StyledSelect>
				</div>
				{destinations.map((destination) => (
					<DestinationUI key={destination.id ?? 0} destination={destination} />
				))}
				<DestinationUI key={destinations.length} destination={null} />
			</div>
		</div>
	);
}
